import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ChallengeFirmaDTO } from '@ausentia/shared';
import { api, ErrorApi } from '../api/cliente.js';
import { useSesion } from '../contexto/Sesion.js';

interface EstadoNav {
  codigo?: string;
  challengeId?: string;
  metodo?: string;
}

export function FirmaProfesional() {
  const navegar = useNavigate();
  const loc = useLocation();
  const { medico } = useSesion();
  const estado = (loc.state as EstadoNav | null) ?? {};
  const [challengeId, setChallengeId] = useState(estado.challengeId ?? '');
  const [codigo] = useState(estado.codigo ?? '');
  const [metodo, setMetodo] = useState(estado.metodo ?? '');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [intentos, setIntentos] = useState<number | null>(null);
  const [expirado, setExpirado] = useState(false);
  const [firmado, setFirmado] = useState(false);
  const [cargando, setCargando] = useState(false);

  // Desde Pendientes se llega sin método en el state: lo resuelve el servidor.
  useEffect(() => {
    if (!metodo && challengeId) {
      api
        .get<{ metodo: string }>(`/api/firmas/${challengeId}`)
        .then((f) => setMetodo(f.metodo))
        .catch(() => {});
    }
  }, [metodo, challengeId]);

  const esClave = metodo === 'MATRICULA_CLAVE';

  if (!challengeId && !firmado) {
    return (
      <div className="contenido">
        <p className="muted">No hay una firma pendiente seleccionada.</p>
        <button className="primario" onClick={() => navegar('/medico/pendientes')}>Ver pendientes</button>
      </div>
    );
  }

  async function autorizar() {
    setError('');
    setCargando(true);
    try {
      await api.post(`/api/firmas/${challengeId}/autorizar`, { otp });
      setFirmado(true);
    } catch (err) {
      if (err instanceof ErrorApi) {
        setError(err.message);
        const d = err.detalles as { intentosRestantes?: number } | undefined;
        if (err.codigo === 'OTP_INCORRECTO' && d) setIntentos(d.intentosRestantes ?? null);
        // CLAVE_FIRMA_NO_DEFINIDA: quitó su clave con este challenge pendiente;
        // el mismo flujo de reintento lo recupera (el nuevo sale por OTP).
        if (err.codigo === 'CHALLENGE_EXPIRADO' || err.codigo === 'CLAVE_FIRMA_NO_DEFINIDA') setExpirado(true);
      } else setError(String(err));
    } finally {
      setCargando(false);
    }
  }

  async function reintentar(forzarEmail = false) {
    setError('');
    setExpirado(false);
    setOtp('');
    try {
      const nuevo = await api.post<ChallengeFirmaDTO>(
        `/api/firmas/${challengeId}/reintentar`,
        forzarEmail ? { metodo: 'OTP_EMAIL' } : undefined,
      );
      setChallengeId(nuevo.challengeId);
      setMetodo(nuevo.metodo);
      setIntentos(nuevo.intentosRestantes);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : String(err));
    }
  }

  if (firmado) {
    return (
      <>
        <div className="barra-titulo"><span>Firma digital</span></div>
        <div className="contenido" style={{ textAlign: 'center', paddingTop: 36 }}>
          <div style={{ fontSize: 52 }}>✅</div>
          <h2>Constancia firmada</h2>
          {codigo && <p className="muted">{codigo}</p>}
          <div className="sello-firma" style={{ textAlign: 'left' }}>
            {esClave
              ? 'Firmada con tu clave personal de matrícula (firma electrónica simple, Ley 25.506 art. 5) y sello de tiempo del servidor.'
              : 'Firmada con PAdES + sello de tiempo TSA (simulado en sandbox).'}{' '}
            Se distribuyó al portal de la empresa y, si el trabajador lo pidió, se envió el enlace seguro por email.
          </div>
          <div className="row-acciones" style={{ flexDirection: 'column' }}>
            <button className="primario" onClick={() => navegar('/medico')}>Volver a las visitas</button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="barra-titulo">
        <button className="volver" onClick={() => navegar('/medico')}>‹</button>
        <span>Firma digital del profesional</span>
      </div>
      <div className="contenido">
        <div className="aviso-info">
          <span>🔏</span>
          <span>
            {esClave ? (
              <>
                Firmá esta operación con tu <strong>clave personal de matrícula</strong>. Es una firma electrónica
                simple (Ley 25.506 art. 5) registrada con sello de tiempo del servidor.
              </>
            ) : (
              <>
                Autorizá la firma <strong>por operación</strong> con el OTP de tu proveedor de firma remota. La clave
                privada permanece en el HSM del certificador; el backend no firma por vos.
              </>
            )}
          </span>
        </div>
        {codigo && <p className="muted" style={{ marginTop: 12 }}>Documento: <strong>{codigo}</strong></p>}
        <p className="muted">Profesional: {medico?.nombre} · {medico?.matricula.tipo} {medico?.matricula.numero}</p>

        <div className="campo" style={{ marginTop: 12 }}>
          <label>{esClave ? 'Clave de firma' : 'Código de autorización (OTP)'}</label>
          <input
            inputMode="numeric"
            type={esClave ? 'password' : 'text'}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
            placeholder="······"
            maxLength={6}
            style={{ letterSpacing: 6, fontSize: 18, textAlign: 'center' }}
          />
          {esClave
            ? <p className="mini">🔑 Ingresá tu clave de firma personal de 6 dígitos (la definiste en tu perfil).</p>
            : metodo === 'OTP_EMAIL'
              ? <p className="mini">📧 Se envió un código de 6 dígitos a tu correo electrónico registrado.</p>
              : medico?.otpSandbox && <p className="mini">Demo · OTP del proveedor sandbox: <strong>{medico.otpSandbox}</strong></p>
          }
          {intentos !== null && <p className="mini" style={{ color: 'var(--rojo)' }}>Intentos restantes: {intentos}</p>}
        </div>

        {error && <div className="error-box">{error}</div>}
        {expirado ? (
          <button className="primario" onClick={() => reintentar()}>Solicitar nuevo código</button>
        ) : (
          <>
            <button className="primario" onClick={autorizar} disabled={cargando || otp.length < 4}>
              {cargando ? 'Autorizando…' : esClave ? 'Firmar con mi clave' : 'Autorizar y firmar'}
            </button>
            {esClave && (
              <button
                className="secundario"
                style={{ marginTop: 8 }}
                onClick={() => reintentar(true)}
                disabled={cargando}
              >
                Prefiero recibir un código por email
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
}
